/**
 * theme.js — Light/dark theme management via localStorage.
 *
 * Theme is stored in localStorage (not Redux) so it's available before
 * React mounts, survives logout, and prevents flash of wrong theme.
 *
 * Values: 'light' | 'dark'
 * Default: browser preference via prefers-color-scheme, fallback to 'light'
 */

const THEME_KEY = 'appTheme';

/**
 * Detect browser/OS preferred color scheme.
 * Returns 'dark' if user prefers dark, 'light' otherwise (including indeterminate).
 */
const detectPreference = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

/**
 * Initialize theme on app startup.
 * Reads localStorage, falls back to browser preference, defaults to light.
 * Applies data-theme attribute to document body.
 */
export const initTheme = () => {
  const stored = localStorage.getItem(THEME_KEY);
  const theme = stored || detectPreference();
  applyTheme(theme);
  return theme;
};

/**
 * Get current theme value.
 */
export const getTheme = () => {
  return localStorage.getItem(THEME_KEY) || detectPreference();
};

/**
 * Set theme and apply it.
 */
export const setTheme = (theme) => {
  localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);
};

/**
 * Toggle between light and dark.
 */
export const toggleTheme = () => {
  const current = getTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
};

/**
 * Apply theme to DOM — sets data-theme on body and dispatches a change event.
 */
const applyTheme = (theme) => {
  // Set on <html> (documentElement) so :root CSS variables resolve correctly
  document.documentElement.setAttribute('data-theme', theme);
  // Also set on body for selectors that target body[data-theme]
  document.body.setAttribute('data-theme', theme);
  // Update meta theme-color for mobile browser chrome
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'dark' ? '#0a0e19' : '#c8c8c8');
  }
  // Notify React components so they can update logo variants, etc.
  window.dispatchEvent(new CustomEvent('themechange', { detail: theme }));
};
